import { constraintConfig } from '../../../constraintConfig';

export const getEffectiveLineItemId = (dataItem: any): number | null => {
    const id = Number(
        dataItem?.expenseItemId ||
            dataItem?.usedExpenseItemId ||
            dataItem?.receiptAtatchedToLineItemID ||
            0
    );
    return id > 0 ? id : null;
};

export const getAttachedReceiptLineItemId = (dataItem: any): number | null => {
    const id = Number(dataItem?.receiptAtatchedToLineItemID || dataItem?.usedExpenseItemId || 0);
    return id > 0 ? id : null;
};

export const getAttachedReceiptExpenseId = (dataItem: any): number | null => {
    const id = Number(
        dataItem?.receiptAtatchedToLineItemExpenseID ||
            (dataItem?.usedExpenseItemId ? dataItem?.expenseId : 0) ||
            0
    );
    return id > 0 ? id : null;
};

export const getAttachedReceiptExpenseTrackingId = (dataItem: any): number | null => {
    const id = Number(
        dataItem?.receiptAtatchedToLineItemExpenseTrackingID ||
            (dataItem?.usedExpenseItemId ? dataItem?.expenseTrackingId : 0) ||
            0
    );
    return id > 0 ? id : null;
};

export const canViewViolations = (dataItem: any): boolean =>
    !!dataItem?.isViolation && !!getEffectiveLineItemId(dataItem);

/** Receipt linked to a line item that is unassigned (-1/null) or on a drafted report (status 1). */
export const canMergeLineItem = (dataItem: any): boolean => {
    if (!dataItem?.expenseItemId) return false;
    if (Number(dataItem?.mergeExpenseitemId) > 0) return false;
    if (dataItem.lineItemRecordSourceId === constraintConfig.recordSourceId.BankFeed) {
        return false;
    }
    const paymentId = dataItem?.expensePayment?.id;
    if (
        paymentId === constraintConfig.paymentMethod.corporateCreditCard ||
        paymentId === constraintConfig.paymentMethod.adcsCorporateCreditCard
    ) {
        return false;
    }
    const expenseId = dataItem.expenseId;
    const notLinkedToReport = expenseId == null || expenseId === -1 || expenseId === 0;
    const isDraftReport = dataItem?.expenseStatus?.id === constraintConfig.expenseStatus.drafted;
    return notLinkedToReport || isDraftReport;
};

/** Linked line item was kept after a merge (MergeExpenseitemId points to From item). */
export const canUnmergeLineItem = (dataItem: any): boolean => {
    if (!dataItem?.expenseItemId) return false;
    return Number(dataItem?.mergeExpenseitemId) > 0;
};

/** Same rules as Receipt Vault v1: editable when not a "used" item and unassigned or Attached. */
export const canEditLineItem = (dataItem: any): boolean => {
    if (!dataItem?.expenseItemId) return false;
    if (dataItem?.usedExpenseItemId) return false;
    return dataItem.expenseId == null || dataItem.ocrReadStatus === 3;
};

/** Super Admin | Org Admin | Support Team | Admin — can see any user's receipts. */
export const canViewAnyUserReceipts = (roleID: number | null | undefined): boolean => {
    if (!roleID) return false;
    return (
        roleID === constraintConfig.roleID.role1 ||
        roleID === constraintConfig.roleID.role2 ||
        roleID === constraintConfig.roleID.roleIDPortalSupport ||
        roleID === constraintConfig.roleID.roleIDAdmin
    );
};

/** Org Admin | Admin | Support Team — OCR diagnostics (Compare OCR, View OCR Text). */
export const canViewOcrDiagnostics = (roleID: number | null | undefined): boolean => {
    if (!roleID) return false;
    return (
        roleID === constraintConfig.roleID.role2 ||
        roleID === constraintConfig.roleID.roleIDAdmin ||
        roleID === constraintConfig.roleID.roleIDPortalSupport
    );
};

/** Nav/page title for /allreceipts: admins see "All Receipts", others see "Receipt Vault". */
export const getAllReceiptsPageTitle = (roleID: number | null | undefined): string =>
    canViewAnyUserReceipts(roleID) ? 'All Receipts' : 'Receipt Vault';

export type AttachedLineItemRemark = {
    reportId?: string;
    date?: string;
    paymentType?: string;
    amount?: string;
    category?: string;
};

const prettyRemarkDate = (raw: string) => {
    const cleaned = (raw || '').replace(/\s+/g, ' ').trim();
    const parsed = new Date(cleaned);
    if (Number.isNaN(parsed.getTime())) return cleaned;
    return parsed.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

/** Parse stored OCR remarks like:
 * "Your receipt has already been attached to the Line Item ( Date: [May 11 2026 7:41PM] | Payment Type: [Reimbursable] | Amount: $41.03)"
 */
export const parseAttachedLineItemRemark = (remarks?: string | null): AttachedLineItemRemark | null => {
    if (!remarks) return null;
    const lineItem = remarks.match(
        /Line Item\s*\(\s*Date:\s*\[([^\]]+)\]\s*\|\s*Payment Type:\s*\[([^\]]+)\]\s*\|\s*Amount:\s*([^)]+)\)/i
    );
    if (lineItem) {
        const reportId = remarks.match(/Report Id\s*\(([^)]+)\)/i)?.[1]?.trim();
        return {
            reportId,
            date: prettyRemarkDate(lineItem[1]),
            paymentType: lineItem[2].trim(),
            amount: lineItem[3].replace(/\)\s*$/, '').trim(),
        };
    }

    const categoryAmount = remarks.match(
        /LineItem,\s*Cat(?:e|a)gory:\s*(.+?)\s+and Amount:\s*(.+)$/i
    );
    if (categoryAmount) {
        const trackingId = remarks.match(/Tracking ID:\s*([A-Za-z0-9\-]+)/i)?.[1]?.trim();
        return {
            reportId: trackingId,
            category: categoryAmount[1].trim(),
            amount: categoryAmount[2].replace(/\)\s*$/, '').trim(),
        };
    }
    return null;
};
